// Latencies the API knows exactly, from released spans, or approximately, from the counts in the
// session summaries (their rollups, D114 #2, APS-445): a session that held its spans on the device
// still sends each endpoint's and screen's p50 and p90, bucketed.
import type { Percentiles } from '../api/types'
import type { Translator } from '../i18n'
import { duration } from './format'

/**
 * A latency (D114, APS-445): from the released spans, or approximate when only the session
 * summaries' counts (their rollups) know it, the spans having stayed on the device.
 */
export interface Latency {
  ms: number
  approx: boolean
}

/** A screen's TTID at a percentile: its spans', else its rollups' (approximate), else none. */
export function ttidAt(
  s:
    | { ttid: Percentiles | null; ttid_approx?: { p50_ms: number; p90_ms: number } | null }
    | undefined,
  key: 'p50_ms' | 'p90_ms',
): Latency | null {
  if (s?.ttid) return { ms: s.ttid[key], approx: false }
  if (s?.ttid_approx) return { ms: s.ttid_approx[key], approx: true }
  return null
}

/** An endpoint's percentile: approximate when none of its spans was released; none without requests. */
export function endpointAt(
  e: { percentiles: 'spans' | 'approximate' | null; p50_ms: number; p90_ms: number } | undefined,
  key: 'p50_ms' | 'p90_ms',
): Latency | null {
  if (!e?.percentiles) return null
  return { ms: e[key], approx: e.percentiles === 'approximate' }
}

/** A latency as text: 412 ms, ≈ 412 ms, or — without one. */
export function latency(i18n: Translator, v: Latency | null | undefined): string {
  if (!v) return '—'
  return v.approx ? `≈ ${duration(i18n, v.ms)}` : duration(i18n, v.ms)
}
